import { Stack, Typography, useTheme } from "@mui/material";
import { isEmpty, isNil } from "lodash-es";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import InlineApiEnumValueList from "@/common/components/Enum/InlineApiEnumValueList";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import ApiEnumIcon from "@/common/components/Icons/ApiEnumIcon";
import { ViewVariant } from "@/common/ts/layout";
import { AppPermission, EntityType, PoolDto, PoolStructureType } from "@/core/api/generated/v1";

import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralEntitySubTypeInline from "../../../General/Display/GeneralEntitySubTypeInline";
import PoolAvailabilityDisplay from "../../PoolAvailabilityDisplay";

interface Props {
  pool: PoolDto;
  onUpdate: (newValue?: PoolDto) => void;
}

export default function OverviewTabContent({ pool, onUpdate }: Props) {
  const theme = useTheme();
  const isMobile = theme.breakpoints.down("md");

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader title={"Details"} subtitle2={<EntityChangedByInfoCard entity={pool} />} />
        }
      >
        <ViewContentBlock>
          <FieldValue label='Name' isEmpty={!pool?.name}>
            {pool?.name}
          </FieldValue>

          <FieldValue label='Description' isEmpty={!pool?.description}>
            {pool?.description}
          </FieldValue>

          <FieldValue
            label='Structure type'
            isEmpty={!pool?.structureType}
            direction={isMobile ? "column" : "row"}
          >
            <InlineApiEnumValue
              type='PoolStructureType'
              value={pool.structureType}
              withDescription
            />
          </FieldValue>

          <FieldValue
            label='Item type'
            isEmpty={!pool?.itemType}
            direction={isMobile ? "column" : "row"}
          >
            <InlineApiEnumValue type='PoolItemType' value={pool.itemType} withDescription />
          </FieldValue>

          {pool?.structureType === PoolStructureType.Uniform && (
            <FieldValue label='Item entity type' isEmpty={!pool?.itemEntityType}>
              <ApiEnumIcon type='EntityType' value={pool?.itemEntityType} inText />{" "}
              <InlineApiEnumValue
                type='PoolItemEntityType'
                value={pool.itemEntityType}
                withDescription={false}
              />
              {pool?.itemEntitySubType && (
                <>
                  {" "}
                  (
                  <Typography component='span' variant='body2'>
                    <GeneralEntitySubTypeInline value={pool?.itemEntitySubType} withIcon />
                  </Typography>
                  )
                </>
              )}
            </FieldValue>
          )}
          {pool?.structureType === PoolStructureType.Diverse && (
            <FieldValue label='Item entity types' isEmpty={isEmpty(pool?.itemEntityTypes)}>
              <InlineApiEnumValueList
                type='PoolItemEntityType'
                values={pool.itemEntityTypes}
                valueProps={{
                  withDescription: false,
                }}
              />
            </FieldValue>
          )}

          <FieldValue label='Availability'>
            <PoolAvailabilityDisplay poolId={pool?.id} availability={undefined} isLoad />
          </FieldValue>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.Pool}
          entityId={pool.id}
          entity={pool}
          entityUpdatePermission={AppPermission.PoolManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        <ViewContentBlock>
          <Typography component='div' variant='h3'>
            Settings
          </Typography>

          <Stack spacing={1}>
            <FieldValue label='Min items' isEmpty={isNil(pool?.settings?.minItems)}>
              {pool.settings?.minItems}
            </FieldValue>

            <FieldValue label='Max items' isEmpty={isNil(pool?.settings?.maxItems)}>
              {pool.settings?.maxItems}
            </FieldValue>

            <FieldValue
              label='Ensure entity belongs to a single pool'
              isEmpty={isNil(pool?.settings?.isEnsureEntityBelongToSinglePool)}
            >
              <BooleanValue value={pool.settings?.isEnsureEntityBelongToSinglePool} />
            </FieldValue>
          </Stack>
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
